import { type FC, useCallback, useMemo, useState } from 'react';
import { Card } from '../../../Card';
import {
  type TeamMembersQueryParams,
  type TeamMembersQueryResponse,
  useTeamMembersQuery,
} from '../../../../apiHooks/useTeamMembersQuery';
import styled from 'styled-components';
import { Pagination } from '../../../Pagination';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import ExportActivityLogButton from '../../../Button/DownloadButton/ExportActivityLogButton/ExportActivityLogButton';
import { useTeamMembersFilters } from '../../hooks/useTeamMembersFilters';
import { ActiveFilterPill } from '../ActiveFilterPill';
import type { TeamMembersUser } from '../../../../lib/types/TeamMembersUser';
import { AvatarInitials } from '../../../AvatarInitials/AvatarInitials';
import { Icon, IconType } from '../../../Props/Icon/Icon';
// import { UserOptionsMenu } from '../UserOptionsMenu';
import { Ellipsis } from '../../../utility/Ellipsis';

const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];
const DEFAULT_ITEMS_PER_PAGE: number = 25 as const;

export const TeamMembers: FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [teamMembersParams, setTeamMembersParams] = useState<TeamMembersQueryParams>({
    page: { skip: 0, take: DEFAULT_ITEMS_PER_PAGE },
    sort: { sortDirection: 1, property: 'cyberiq' },
    filters: [],
  });

  const { data: teamMembersQueryResponse } = useTeamMembersQuery(teamMembersParams);
  const teamMembersData: TeamMembersQueryResponse = teamMembersQueryResponse ?? {
    total: 0,
    skip: null,
    take: null,
    values: [],
  };

  const onNewParams = useCallback(
    (params: Partial<TeamMembersQueryParams>, take: number = itemsPerPage) => {
      if (params.filters) {
        // Reset page filters
        params.page = { skip: 0, take };
        setCurrentPage(1);
      }

      setTeamMembersParams((prev) => {
        let mergedFilters = [...(prev.filters || [])];

        if (params.filters) {
          if (params.filters.length === 0) {
            mergedFilters = [];
          } else {
            params.filters.forEach((newFilter) => {
              mergedFilters = mergedFilters.filter((filter) => filter.property !== newFilter.property);
              if (newFilter.value !== '') {
                mergedFilters.push(newFilter);
              }
            });
          }
        }

        return {
          ...prev,
          ...params,
          filters: mergedFilters,
        };
      });
    },
    [itemsPerPage],
  );

  const { clearFilters } = useTeamMembersFilters(onNewParams);

  const handlePageChange = (page: number) => {
    onNewParams({ page: { take: itemsPerPage, skip: (page - 1) * itemsPerPage } });
    setCurrentPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    onNewParams({ page: { skip: 0, take: pageSize } }, pageSize);
  };

  const members = useMemo(() => teamMembersData.values, [teamMembersData.values]);

  return (
    <StyledCard withShadow withBorder>
      <TeamMembersSection>
        <SectionHeader>
          <HeaderMain>
            <Title>
              <NamedContentEntryText refKey="managerSectionTitles" container="span" subKey="teamMembers" />
            </Title>
            {!!teamMembersParams.filters?.length && (
              <ActiveFilterPill onClear={clearFilters} filterCount={teamMembersData.total} />
            )}
          </HeaderMain>
          <ExportActivityLogButton />
        </SectionHeader>
        <TableViewport>
          <MembersTable>
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '27%' }} />
              <col style={{ width: '5%' }} />
            </colgroup>
            <thead>
              <HeaderRow>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="name" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="jobTitle" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="department" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="cyberIq" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="training" />
                </HeaderCell>
                {/* <HeaderActionCell /> */}
              </HeaderRow>
            </thead>
            <tbody>
              {members.map((user) => {
                const progress = getTrainingProgress(user);
                return (
                  <BodyRow key={user.learnerId}>
                    <BodyCell>
                      <MemberIdentity>
                        {user.avatar ? (
                          <AvatarImage src={`data:image/png;base64,${user.avatar}`} alt={user.name} />
                        ) : (
                          <AvatarInitials name={user.name || ''} id={user.learnerId} size="28px" />
                        )}
                        <MemberName>
                          <Ellipsis>{user.name}</Ellipsis>
                        </MemberName>
                      </MemberIdentity>
                    </BodyCell>
                    <BodyCell>
                      <MutedText>
                        <Ellipsis>{user.jobTitle}</Ellipsis>
                      </MutedText>
                    </BodyCell>
                    <BodyCell>
                      <MutedText>
                        <Ellipsis>{user.departmentId}</Ellipsis>
                      </MutedText>
                    </BodyCell>
                    <BodyCell>
                      <PointsCell>
                        <TrendMarker $trend="flat" />
                        <Icon width={16} height={16} icon={IconType.COIN} />
                        <PointsValue>{formatPoints(user.cyberIqPoints)}</PointsValue>
                      </PointsCell>
                    </BodyCell>
                    <BodyCell>
                      <TrainingCell>
                        <TrainingTrack>
                          <TrainingFill $progress={progress} $color={getTrainingColor(progress)} />
                        </TrainingTrack>
                        <TrainingCount>
                          {user.trainings?.completed ?? 0}/{user.trainings?.assigned ?? 0}
                        </TrainingCount>
                      </TrainingCell>
                    </BodyCell>
                    {/* <ActionCell>
                      <UserOptionsMenu />
                    </ActionCell> */}
                  </BodyRow>
                );
              })}
            </tbody>
          </MembersTable>
        </TableViewport>
        <Pagination
          pages={Math.ceil(teamMembersData.total / itemsPerPage)}
          currentPage={currentPage}
          perPage={itemsPerPage}
          listSize={teamMembersData.total}
          onChange={handlePageChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={handlePageSizeChange}
        />
      </TeamMembersSection>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  padding: 24px;
  & > section {
    margin-top: 0;
  }
`;

const getTrainingProgress = (user: TeamMembersUser) => {
  if (!user.trainings || user.trainings.assigned === 0) {
    return 0;
  }

  return (user.trainings.completed / user.trainings.assigned) * 100;
};

const getTrainingColor = (progress: number) => {
  if (progress > 60) {
    return 'primary';
  }

  if (progress > 40) {
    return 'managerPrimary';
  }

  return 'error';
};

const formatPoints = (value: number) => value.toLocaleString('en-US');

const TeamMembersSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TableViewport = styled.div`
  overflow: visible;

  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: visible;
    margin: 0 -4px;
  }
`;

const MembersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 760px;
  }
`;

const withAlpha = (color: string, alpha: number) => color.replace(/\)\s*$/, ` / ${alpha})`);

const HeaderRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.prototype.border};
`;

const HeaderCell = styled.th`
  padding: 0 0 14px;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

// const HeaderActionCell = styled(HeaderCell)`
//   width: 32px;
//   text-align: right;
//   margin-right: 4px;
// `;

const BodyRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => withAlpha(theme.colors.prototype.border, 0.5)};
  transition: background-color 200ms ease;

  &:hover {
    background-color: ${({ theme }) => withAlpha(theme.colors.prototype.secondary, 0.3)};
  }

  & > td:last-child {
    padding-right: 5px;
  }
`;

const BodyCell = styled.td`
  padding: 8px 0;
  vertical-align: middle;
`;

const MemberIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const AvatarImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
`;

const MemberName = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const MutedText = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const PointsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.prototype.foreground};

  > svg {
    flex: none;
  }
`;

const TrendMarker = styled.span<{ $trend: 'up' | 'down' | 'flat' }>`
  display: inline-flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  color: ${({ $trend, theme }) =>
    $trend === 'up'
      ? theme.colors.prototype.success
      : $trend === 'down'
        ? theme.colors.prototype.destructiveText
        : theme.colors.prototype.mutedForeground};
  flex: none;

  &::before {
    content: ${({ $trend }) => ($trend === 'up' ? "'↗'" : $trend === 'down' ? "'↘'" : "'−'")};
    font-size: ${({ theme }) => theme.fontSizes.normal};
    line-height: 1;
  }
`;

const PointsValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const TrainingCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrainingTrack = styled.div`
  width: 50%;
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.prototype.secondary};
  overflow: hidden;
`;

const TrainingFill = styled.div<{ $progress: number; $color: 'primary' | 'managerPrimary' | 'error' }>`
  height: 100%;
  width: ${({ $progress }) => `${Math.max(0, Math.min(100, $progress))}%`};
  border-radius: 999px;
  background: ${({ $color, theme }) => theme.colors[$color]};
`;

const TrainingCount = styled(MutedText)`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

// const ActionCell = styled(BodyCell)`
//   width: 32px;
//   text-align: left;
// `;
